import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'
import ApiClient from '@src/lib/api_client'
import async from 'async'

import { Input } from '@src/elements/input/input'
import { DatePicker } from '@src/elements/input/date-picker'
import { bulletColorMapping } from '@src/util/constants'
import { Radio } from '@src/elements/input/radio'
import { getDaysDifference, snakecaseKeys } from '@src/util/functions'
import { validateMinMaxValue, validateRequiredField } from '@src/util/validation'
import { FormInputRow } from '@src/routes/members/components/body-measurements/form-input-row'
import { Textarea } from '@src/elements/input/textarea'
import { Bullet } from '@src/elements/bullet/bullet'
import EventBus from '@src/lib/event_bus'
import { API } from '@src/lib/api_path'
import Member from '@src/data/member'
import { Button } from '@src/elements/button/button'
import { convertToLocalDate, convertLocalDateToDate } from '@src/util/date-format'

import './form.sass'
import { convertToCM, convertToKg, LengthUnit, WeightUnit } from '@src/util/weight'
import SessionManager from '@src/lib/session_manager'

@Component({
  computed: {
    member () {
      return this.$store.state.selectedMember
    },
    weightUnit () {
      return this.$store.state.club.weightUnit.toUpperCase()
    },
    lengthUnit () {
      return this.$store.state.club.weightUnit === WeightUnit.KG ? LengthUnit.CM : LengthUnit.IN
    }
  }
})
export class Form extends Vue {

  @Prop({ required: false })
  notes: object

  protected member: Member

  protected weightUnit: WeightUnit
  protected lengthUnit: LengthUnit

  protected newMeasurements: any
  protected initialMeasurements: any

  protected translationKey: string = ''

  protected apiClient: ApiClient

  private selectedMeasuringUnits = {
    body_weight: 'KG',
    body_fat: 'KG',
    muscle_mass: 'KG',
    body_water_mass: 'KG'
  }

  private noteValue: string = ''
  private noteKey: string = ''

  private fieldErrors: string[] = []

  constructor () {
    super()

    this.apiClient = new ApiClient()
  }

  getInitialMeasuringUnits () {
    return {
      body_weight: this.weightUnit,
      body_fat: this.weightUnit,
      muscle_mass: this.weightUnit,
      body_water_mass: this.weightUnit
    }
  }

  created () {
    this.selectedMeasuringUnits = this.getInitialMeasuringUnits()
    if (this.notes) {
      this.noteKey = Object.keys(this.notes)[0]
      this.noteValue = this.notes[this.noteKey]
    }
  }

  save () {
    this.saveBodyMeasurement()
    this.saveNotes()
  }

  saveBodyMeasurement () {
    // validate both values in blood pressure
    this.validateBloodPressure()
    if (this.fieldErrors.length === 0) {
        // format each entry
      let measurements = this.formatMeasurements()

        // submit
      this.submitMeasurements(measurements)
    }
  }

  checkIfThereIsNewValue () {
    // if at least 2 field has value, submit it (1 field always is the date)
    return this.countNewValues(this.newMeasurements) > 1
  }

  countNewValues (measurements) {
    let total = 0
    Object.values(measurements).forEach((value) => {
      if (value) typeof value === 'object' ? total += this.countNewValues(value) : total++
    })
    return total
  }

  formatMeasurements () {
    let measurements: any[] = []
    let metrics = Object.assign({}, this.convertToDefaultUnit())

    const effectiveAt = this.buildTimestamp(convertLocalDateToDate(metrics.date))

    Object.keys(metrics).forEach(key => {
      if (key !== 'date' && metrics[key]) {
        switch (key) {
          case 'measurement':
            measurements = [...measurements, ...this.buildBodyCompositionMeasurements(metrics[key], effectiveAt)]
            break
          case 'systolic_diastolic':
            measurements = [...measurements, ...this.buildBloodPressureMeasurements(metrics[key], effectiveAt)]
            break
          default:
            let value = metrics[key]
            if (['body_weight', 'body_fat', 'muscle_mass', 'body_water_mass', 'body_vfat'].indexOf(key) >= 0) {
              value = convertToKg(value, this.weightUnit)
            }
            if (['waistline', 'right_bicep_circumference', 'left_bicep_circumference', 'right_thigh_circumference', 'left_thigh_circumference', 'hip_measurement', 'chest_circumference'].indexOf(key) >= 0) {
              value = convertToCM(value, this.lengthUnit)
            }
            measurements.push({ body_measurement: { metric: key, value, effective_at: effectiveAt } })
        }
      }
    })

    return measurements
  }

  submitMeasurements (measurements) {
    async.waterfall(
      measurements.map((measurement) => {
        return (done) => this.submitMeasurement(measurement, done)
      }),
      () => {
        EventBus.trigger('reloadBodyMeasurements')
        this.resetForm()
        return this.$store.dispatch('showToast', { message: this.$t('BODY_MEASUREMENTS.SAVE_SUCCESS'), fadeOut: true })
      })
  }

  submitMeasurement (measurement, done) {
    return this.apiClient
      .post(API.TRAINERS_API.BODY_MEASUREMENTS.replace('{memberId}', `${this.member.id}`), measurement)
      .catch((error) => console.log('cannot save body measurement', error))
      .finally(() => done(null))
  }

  convertToDefaultUnit () {
    // convert all % values to kg
    let measurements = this.newMeasurements
    Object.keys(measurements).forEach(key => {
      if (this.newMeasurements[key] && this.selectedMeasuringUnits[key] === '%') {
        measurements[key] = (measurements[key] * measurements.body_weight / 100).toFixed(2)
      }
    })
    return measurements
  }

  buildBodyCompositionMeasurements (data: any, date: string): any[] {
    let measurements: any[] = []
    Object.keys(data).forEach(key => {
      if (data[key].muscle) {
        if (data[key].muscle.weight) {
          measurements.push({ body_measurement: { metric: `${key}_muscle_mass`, value: convertToKg(data[key].muscle.weight, this.weightUnit), effective_at: date } })
        }
        if (data[key].muscle.percent) {
          measurements.push({ body_measurement: { metric: `${key}_muscle_percentage`, value: data[key].muscle.percent, effective_at: date } })
        }
      }
      if (data[key].fat) {
        if (data[key].fat.weight) {
          measurements.push({ body_measurement: { metric: `${key}_fat_mass`, value: convertToKg(data[key].fat.weight, this.weightUnit), effective_at: date } })
        }
        if (data[key].fat.percent) {
          measurements.push({ body_measurement: { metric: `${key}_fat_percentage`, value: data[key].fat.percent, effective_at: date } })
        }
      }
    })
    return measurements
  }

  buildBloodPressureMeasurements (data: any, date: string): any[] {
    let measurements: any[] = []
    if (data.systolic_blood_pressure) {
      measurements.push({ body_measurement: { metric: 'systolic_blood_pressure', value: data.systolic_blood_pressure, effective_at: date } })
    }
    if (data.diastolic_blood_pressure) {
      measurements.push({ body_measurement: { metric: 'diastolic_blood_pressure', value: data.diastolic_blood_pressure, effective_at: date } })
    }
    return measurements
  }

  buildTimestamp (date) {
    const now = new Date()
    if (!(date instanceof Date) || isNaN(date.getTime()) || getDaysDifference(date, new Date()) === 0) {
      // invalid date or today
      return now.toISOString()
    }

    // add the current time to be able to differentiate between multiple metrics added to the same day in the past
    date.setHours(now.getHours(), now.getMinutes(), now.getSeconds())
    return date.toISOString()
  }

  saveNotes () {
    if (this.notes && this.noteValue !== this.notes[this.noteKey]) {
      let memberNote = {}
      memberNote[this.noteKey] = this.noteValue
      this.apiClient.put(API.TRAINERS_API.MEMBER_DETAILS.replace('{memberId}', `${this.member.id}`), { member: snakecaseKeys(memberNote) })
        .catch((error) => console.log('cannot update member', error))
    }
  }

  resetForm () {
    this.selectedMeasuringUnits = this.getInitialMeasuringUnits()
    this.newMeasurements = { ...this.initialMeasurements }
  }

  validateBloodPressure () {
    // both values must be filled in and between 0 and 300
    if (this.newMeasurements && (this.newMeasurements.systolic_blood_pressure || this.newMeasurements.diastolic_blood_pressure)) {
      const systolicIndex = this.fieldErrors.indexOf('systolic_blood_pressure')
      const isSystolicValid = !validateRequiredField(this.newMeasurements.systolic_blood_pressure)
      if (!isSystolicValid && systolicIndex < 0) {
        this.fieldErrors.push('systolic_blood_pressure')
      } else if (isSystolicValid && systolicIndex >= 0) {
        this.fieldErrors.splice(systolicIndex, 1)
      }
      const diastolicIndex = this.fieldErrors.indexOf('diastolic_blood_pressure')
      const isDiastolicValid = !validateRequiredField(this.newMeasurements.diastolic_blood_pressure)
      if (!isDiastolicValid && diastolicIndex < 0) {
        this.fieldErrors.push('diastolic_blood_pressure')
      } else if (isDiastolicValid && diastolicIndex >= 0) {
        this.fieldErrors.splice(diastolicIndex, 1)
      }
    }
  }

  validateValue (value, fieldKey, measuringUnit?) {
    let isValid
    if (value === 'NaN') value = ''
    if (!value) {
      isValid = true
    } else if (measuringUnit === '%') {
      isValid = validateMinMaxValue(value, 1, 100)
      // % of the body measurements is based on the weight:
      if (this.newMeasurements.body_weight && validateRequiredField((this.newMeasurements.body_weight || /* istanbul ignore next: the weight is checked just before */ '').toString())) {
        this.fieldErrors.push('body_weight')
      }
    } else if (fieldKey === 'body_vfat') {
      isValid = validateMinMaxValue(value, 0, 60)
    } else {
      isValid = validateMinMaxValue(value, 1, 1000)
    }

    const index = this.fieldErrors.indexOf(fieldKey)
    if (!isValid && index < 0) {
      this.fieldErrors.push(fieldKey)
    } else if (isValid && index >= 0) {
      this.fieldErrors.splice(index, 1)
    }
    if (fieldKey === 'body_weight' && !value) {
      this.selectedMeasuringUnits = this.getInitialMeasuringUnits()
    }

    return value
  }

  convertValue (fieldKey, measuringUnit) {
    if (fieldKey === 'body_weight') return
    if (measuringUnit === '') {
      // on uncheck, reverse
      this.selectedMeasuringUnits[fieldKey] = this.selectedMeasuringUnits[fieldKey] === this.weightUnit ? '%' : this.weightUnit
    } else {
      // set the selected
      this.selectedMeasuringUnits[fieldKey] = measuringUnit
    }

    switch (this.selectedMeasuringUnits[fieldKey]) {
      case '%':
        if (validateRequiredField((this.newMeasurements.body_weight || '').toString())) {
          this.fieldErrors.push('body_weight')
          this.newMeasurements[fieldKey] = null
        }
        if (this.newMeasurements[fieldKey]) this.newMeasurements[fieldKey] = `${+((this.newMeasurements[fieldKey] * 100 / this.newMeasurements.body_weight).toFixed(2))}`
        break
      case this.weightUnit:
        this.fieldErrors.splice(this.fieldErrors.indexOf('body_weight'), 1)
        if (!this.newMeasurements['body_weight']) {
          this.newMeasurements[fieldKey] = null
        }
        if (this.newMeasurements[fieldKey]) this.newMeasurements[fieldKey] = `${+((this.newMeasurements[fieldKey] * this.newMeasurements.body_weight / 100).toFixed(2))}`
        break
      default:
        console.log('unhandled measuring unit')
    }
  }

  renderDateInputRow (h: CreateElement) {
    return <FormInputRow>
      <DatePicker type='date' value={this.newMeasurements.date} change={(value) => this.newMeasurements.date = value} allowFutureDate={false} label={this.$t('DATE')} />
    </FormInputRow>
  }

  renderNotesRow (h: CreateElement) {
    return <FormInputRow>
      <Textarea value={this.noteValue} label={this.$t('NOTES')} change={(value) => this.noteValue = value} ref={`notes`} />
    </FormInputRow>
  }

  renderInputRow (h: CreateElement, key) {
    let measureUnit = ''
    if (['waistline', 'right_bicep_circumference', 'left_bicep_circumference', 'right_thigh_circumference', 'left_thigh_circumference', 'hip_measurement', 'chest_circumference'].indexOf(key) >= 0) {
      measureUnit = '/' + this.$t(`UNITS.${this.lengthUnit.toUpperCase()}`)
    }
    return <FormInputRow>
      <Input type='decimal' value={this.newMeasurements[key] ? this.newMeasurements[key].toString() : ''}
             htmlLabel={<div><Bullet color={bulletColorMapping[key]} />{this.$t(this.translationKey + key.toUpperCase()) + measureUnit}</div>}
             valueChanged={(value) => this.newMeasurements[key] = this.validateValue(value, key)} class={`${this.fieldErrors.indexOf(key) >= 0 ? 'error' : ''}`}
             ref={`${key}Input`} />
    </FormInputRow>
  }

  renderBloodPressureInputRow (h: CreateElement) {
    return <FormInputRow>
      <Input type='number' value={this.newMeasurements.systolic_blood_pressure}
             label={`${this.$t(this.translationKey + 'SYSTOLIC_BLOOD_PRESSURE')}/${this.$t('UNITS.MMHG')}`}
             valueChanged={(value) => this.newMeasurements.systolic_blood_pressure = this.validateValue(value, 'systolic_blood_pressure')}
             class={`${this.fieldErrors.indexOf('systolic_blood_pressure') >= 0 ? 'error' : ''}`} ref={`systolicInput`} />
      <Input type='number' value={this.newMeasurements.diastolic_blood_pressure.toString()}
             label={`${this.$t(this.translationKey + 'DIASTOLIC_BLOOD_PRESSURE')}/${this.$t('UNITS.MMHG')}`}
             valueChanged={(value) => this.newMeasurements.diastolic_blood_pressure = this.validateValue(value, 'diastolic_blood_pressure')}
             class={`${this.fieldErrors.indexOf('diastolic_blood_pressure') >= 0 ? 'error' : ''}`} ref={`diastolicInput`} />
    </FormInputRow>
  }

  renderRowWithMeasuringUnitsSwitch (h: CreateElement, key) {
    return <FormInputRow>
      <Input type='decimal' value={this.newMeasurements[key]}
             htmlLabel={<div><Bullet color={bulletColorMapping[key]} />{this.$t(this.translationKey + key.toUpperCase())}</div>}
             valueChanged={(value) => this.newMeasurements[key] = this.validateValue(value, key, this.selectedMeasuringUnits[key])}
             class={`${this.fieldErrors.indexOf(key) >= 0 ? 'error' : ''}`} ref={`${key}Input`} />
      <el-radio-group slot='units'>
        <Radio value={this.weightUnit} text={this.$t(`UNITS.${this.weightUnit}`)} bindedProp={this.selectedMeasuringUnits[key]} change={(value) => this.convertValue(key, value)} ref={this.weightUnit.toLowerCase()} />
        {key === 'body_weight' ? '' : <Radio value='%' text='%' bindedProp={this.selectedMeasuringUnits[key]} change={(value) => this.convertValue(key, value)} ref='percentage' />}
      </el-radio-group>
    </FormInputRow>
  }

  renderBodyCompositionInputRow (h: CreateElement, bodyCompositionKey) {
    return <FormInputRow>
      <span slot='label' class={'bullet ' + bulletColorMapping[bodyCompositionKey]}>{this.$t(this.translationKey + bodyCompositionKey.toUpperCase())}</span>
      <Input type='decimal' label={this.$t(`UNITS.${this.weightUnit}`)}
             value={`${this.newMeasurements.measurement[bodyCompositionKey].muscle.weight}`}
             valueChanged={(value) => this.newMeasurements.measurement[bodyCompositionKey].muscle.weight = this.validateValue(value, `${bodyCompositionKey}muscle.weight`)}
             class={`${this.fieldErrors.indexOf(`${bodyCompositionKey}muscle.weight`) >= 0 ? 'error' : ''}`} ref={`${bodyCompositionKey}MuscleWeightInput`} />
      <Input type='decimal' label='%'
             value={`${this.newMeasurements.measurement[bodyCompositionKey].muscle.percent}`}
             valueChanged={(value) => this.newMeasurements.measurement[bodyCompositionKey].muscle.percent = this.validateValue(value, `${bodyCompositionKey}muscle.percent`, '%')}
             class={`${this.fieldErrors.indexOf(`${bodyCompositionKey}muscle.percent`) >= 0 ? 'error' : ''}`} ref={`${bodyCompositionKey}MusclePercentInput`} />
      <Input type='decimal' label={this.$t(`UNITS.${this.weightUnit}`)}
             value={`${this.newMeasurements.measurement[bodyCompositionKey].fat.weight}`}
             valueChanged={(value) => this.newMeasurements.measurement[bodyCompositionKey].fat.weight = this.validateValue(value, `${bodyCompositionKey}fat.weight`)}
             class={`${this.fieldErrors.indexOf(`${bodyCompositionKey}fat.weight`) >= 0 ? 'error' : ''}`} ref={`${bodyCompositionKey}FatWeightInput`} />
      <Input type='decimal' label='%'
             value={`${this.newMeasurements.measurement[bodyCompositionKey].fat.percent}`}
             valueChanged={(value) => this.newMeasurements.measurement[bodyCompositionKey].fat.percent = this.validateValue(value, `${bodyCompositionKey}fat.percent`, '%')}
             class={`${this.fieldErrors.indexOf(`${bodyCompositionKey}fat.percent`) >= 0 ? 'error' : ''}`} ref={`${bodyCompositionKey}FatPercentInput`} />
    </FormInputRow>
  }

  renderSaveButton (h: CreateElement) {
    if (SessionManager.Instance.isManager()) {
      return <Button ref='saveBtn' label={this.$t('SAVE')} click={this.save} type='elevated' class='save-btn' disabled={!this.isSaveEnabled()}/>
    }
    return null
  }

  isSaveEnabled () {
    return this.fieldErrors.length === 0 && this.checkIfThereIsNewValue()
  }
}

@Component
export class WeightForm extends Form {

  constructor () {
    super()
    const initialMeasurements = {
      body_weight: null,
      body_fat: null,
      muscle_mass: null,
      body_water_mass: null,
      body_vfat: null,
      date: convertToLocalDate(new Date())
    }
    this.initialMeasurements = Object.assign({}, initialMeasurements)
    this.newMeasurements = Object.assign({}, initialMeasurements)
    this.translationKey = 'BODY_MEASUREMENTS.WEIGHT.'
  }

  render (h: CreateElement) {
    return <div class='add-body-measurement-form weight'>
      <div class={'measurement-inputs-alignment-on-mobile'}>
        {this.renderRowWithMeasuringUnitsSwitch(h, 'body_weight')}
        {this.renderRowWithMeasuringUnitsSwitch(h, 'body_fat')}
        {this.renderRowWithMeasuringUnitsSwitch(h, 'muscle_mass')}
        {this.renderRowWithMeasuringUnitsSwitch(h, 'body_water_mass')}
        {this.renderInputRow(h, 'body_vfat')}
        {this.renderDateInputRow(h)}
      </div>
      {this.renderNotesRow(h)}
      {this.renderSaveButton(h)}
    </div>
  }
}

@Component
export class CircumferencesForm extends Form {

  constructor () {
    super()
    const initialMeasurements = {
      waistline: null,
      right_bicep_circumference: null,
      left_bicep_circumference: null,
      right_thigh_circumference: null,
      left_thigh_circumference: null,
      hip_measurement: null,
      chest_circumference: null,
      date: convertToLocalDate(new Date())
    }
    this.initialMeasurements = Object.assign({}, initialMeasurements)
    this.newMeasurements = Object.assign({}, initialMeasurements)
    this.translationKey = 'BODY_MEASUREMENTS.CIRCUMFERENCE.'
  }

  render (h: CreateElement) {
    return <div class='add-body-measurement-form circumferences'>
      <div class={'measurement-inputs-alignment-on-mobile'}>
        {this.renderInputRow(h, 'waistline')}
        {this.renderInputRow(h, 'right_bicep_circumference')}
        {this.renderInputRow(h, 'left_bicep_circumference')}
        {this.renderInputRow(h, 'right_thigh_circumference')}
        {this.renderInputRow(h, 'left_thigh_circumference')}
        {this.renderInputRow(h, 'hip_measurement')}
        {this.renderInputRow(h, 'chest_circumference')}
        {this.renderDateInputRow(h)}
      </div>
      {this.renderNotesRow(h)}
      {this.renderSaveButton(h)}
    </div>
  }
}

@Component
export class BloodPressureForm extends Form {

  constructor () {
    super()
    const initialMeasurements = {
      systolic_blood_pressure: '',
      diastolic_blood_pressure: '',
      date: convertToLocalDate(new Date())
    }
    this.initialMeasurements = Object.assign({}, initialMeasurements)
    this.newMeasurements = Object.assign({}, initialMeasurements)
    this.translationKey = 'BODY_MEASUREMENTS.BLOOD_PRESSURE_CARD.'
  }

  render (h: CreateElement) {
    return <div class='add-body-measurement-form blood-pressure'>
      <div class={'measurement-inputs-alignment-on-mobile'}>
        {this.renderBloodPressureInputRow(h)}
        {this.renderDateInputRow(h)}
      </div>
      {this.renderNotesRow(h)}
      {this.renderSaveButton(h)}
    </div>
  }
}

@Component
export class BodyCompositionForm extends Form {

  constructor () {
    super()
    const initialMeasurements = {
      measurement: {
        trunk: { muscle: { weight: '', percent: '' }, fat: { weight: '', percent: '' } },
        left_arm: { muscle: { weight: '', percent: '' }, fat: { weight: '', percent: '' } },
        right_arm: { muscle: { weight: '', percent: '' }, fat: { weight: '', percent: '' } },
        left_leg: { muscle: { weight: '', percent: '' }, fat: { weight: '', percent: '' } },
        right_leg: { muscle: { weight: '', percent: '' }, fat: { weight: '', percent: '' } }
      },
      date: convertToLocalDate(new Date())
    }
    this.initialMeasurements = Object.assign({}, initialMeasurements)
    this.newMeasurements = Object.assign({}, initialMeasurements)
    this.translationKey = 'BODY_MEASUREMENTS.BODY_COMPOSITION_LABELS.'
  }

  resetForm () {
    this.newMeasurements = {
      measurement: {
        trunk: { muscle: { weight: '', percent: '' }, fat: { weight: '', percent: '' } },
        left_arm: { muscle: { weight: '', percent: '' }, fat: { weight: '', percent: '' } },
        right_arm: { muscle: { weight: '', percent: '' }, fat: { weight: '', percent: '' } },
        left_leg: { muscle: { weight: '', percent: '' }, fat: { weight: '', percent: '' } },
        right_leg: { muscle: { weight: '', percent: '' }, fat: { weight: '', percent: '' } }
      },
      date: convertToLocalDate(new Date())
    }
  }

  render (h: CreateElement) {
    const rows = Object.keys(this.newMeasurements.measurement).map(bodyCompositionKey => {
      return this.renderBodyCompositionInputRow(h, bodyCompositionKey)
    })

    return <div class='add-body-measurement-form body-composition'>
      <div class='column-header'>
        <span class='input-label' />
        <div>
          <span>{this.$t('BODY_MEASUREMENTS.BODY_COMPOSITION_LABELS.MUSCLE')}</span>
          <span>{this.$t('BODY_MEASUREMENTS.BODY_COMPOSITION_LABELS.FAT')}</span>
        </div>
      </div>

      {rows}
      {this.renderDateInputRow(h)}
      {this.renderSaveButton(h)}
    </div>
  }
}

@Component
export class NotesForm extends Form {

  constructor () {
    super()
  }

  render (h: CreateElement) {
    return <div class='add-body-measurement-form'>
      {this.renderNotesRow(h)}
    </div>
  }
}
